/**
 * The admin area (SA-963, spec docs/superpowers/specs/2026-10-06-sidebar-navigation-design.md
 * §4): the sidebar's one Admin row opens it, and every /admin/* page renders in
 * its outlet at its own path. The rows are the navigation model's `adminArea`
 * (the viewer's gates, empty groups dropped); this component decides no
 * visibility of its own, and the routes keep their own guards (App.tsx).
 *
 * At xl a 208px column of text rows sits beside the page (lg left the page
 * 488px wide at 1024px; SA-978). Nothing renders above the outlet there,
 * because a page's StickyPageHeader assumes it is the first thing in <main>'s
 * padding box. Below xl a native select takes the column's place in a band
 * whose margin is the one a StickyPageHeader's negative top margin lands in:
 * mb-4 under its -mt-4, mb-6 from lg under its -mt-6.
 */
import { useLayoutEffect, useRef, useState, type RefObject } from 'react'
import { NavLink, Navigate, Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { cn } from '@/utils'
import { useNavModel } from '@/components/layout/useNavModel'
import type { NavGroup, NavRow, NavSurface } from '@/components/layout/navModel'

/** What the area hands its outlet: the index redirects to `firstRow`. */
export interface AdminAreaContext {
  firstRow: string | null
}

/**
 * The open page's row: its own path or a page under it (spec §1.5), the longest
 * match winning. Compared in lower case, as the router matches a route.
 */
function openRow(groups: NavGroup[], pathname: string): NavRow | null {
  const path = pathname.toLowerCase()
  let found: NavRow | null = null
  for (const row of groups.flatMap((g) => g.rows)) {
    const to = row.to.toLowerCase()
    const open = path === to || path.startsWith(`${to}/`)
    if (open && (!found || row.to.length > found.to.length)) found = row
  }
  return found
}

/**
 * The height <main> shows below its padding: the most a column pinned at its
 * top can show at once. Measured, because what sits above <main> varies (the
 * View-as banner; below lg, the phone's top bar) and so does the window.
 */
function useMainContentHeight(ref: RefObject<HTMLElement | null>, active: boolean): number | undefined {
  const [height, setHeight] = useState<number>()
  useLayoutEffect(() => {
    const main = ref.current?.closest('main')
    if (!active || !main) return
    const measure = () => {
      const style = getComputedStyle(main)
      setHeight(main.clientHeight - parseFloat(style.paddingTop || '0') - parseFloat(style.paddingBottom || '0'))
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(main)
    return () => observer.disconnect()
  }, [ref, active])
  return height
}

/**
 * The select below xl (spec §4.2). A click or tap on an option goes to its page.
 * On Windows a key on the CLOSED select (an arrow, a typed letter) changes the
 * value and fires `change` within that keydown's own task, so such a change
 * only shows the option: Enter goes there and leaving the select puts it back
 * (WCAG 3.2.2, SA-978). The key's mark lasts one task, because macOS opens its
 * popup on the key instead and the choice made there arrives later, with no
 * keydown of its own: that change goes at once, as a click's does.
 */
function AdminPageSelect({ groups, open }: { groups: NavGroup[]; open: NavRow | null }) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const keyed = useRef(false)
  const [waiting, setWaiting] = useState<{ on: string; to: string } | null>(null)
  const shown = waiting?.on === pathname ? waiting.to : null

  const go = (to: string) => {
    setWaiting(null)
    if (to !== open?.to) navigate(to)
  }

  return (
    <select
      aria-label="Admin page"
      value={shown ?? open?.to ?? ''}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          if (shown !== null) {
            e.preventDefault()
            go(shown)
          }
          return
        }
        keyed.current = true
        setTimeout(() => {
          keyed.current = false
        }, 0)
      }}
      onChange={(e) => {
        if (keyed.current) setWaiting({ on: pathname, to: e.target.value })
        else go(e.target.value)
      }}
      onBlur={() => setWaiting(null)}
      className="w-full h-9 px-3 text-sm rounded-md border border-border bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring"
    >
      {!open && <option value="" disabled>Choose a page</option>}
      {groups.map((group) => (
        <optgroup key={group.id} label={group.label ?? ''}>
          {group.rows.map((row) => (
            <option key={row.id} value={row.to}>{row.label}</option>
          ))}
        </optgroup>
      ))}
    </select>
  )
}

export function AdminArea({ surfaces }: { surfaces: readonly NavSurface[] }) {
  const groups = useNavModel(surfaces).adminArea
  const { pathname } = useLocation()
  const open = openRow(groups, pathname)
  const context: AdminAreaContext = { firstRow: groups[0]?.rows[0]?.to ?? null }
  const navRef = useRef<HTMLElement>(null)
  const maxHeight = useMainContentHeight(navRef, groups.length > 0)

  return (
    <div data-testid="admin-area" className="xl:flex xl:gap-6">
      {groups.length > 0 && (
        <>
          <div className="mb-4 lg:mb-6 xl:hidden">
            <AdminPageSelect groups={groups} open={open} />
          </div>
          {/* Pinned, and its own scroll area no taller than <main>'s content
              box: a pinned column taller than the screen would hide its last
              rows until the page's end pushed it up. space-y-3 keeps 16 rows in 5
              groups on one 1280x720 screen (706px): compass-staging's set since
              SA-1054's Airports row; the full profile's 17 scroll by about a row. */}
          <nav
            ref={navRef}
            aria-label="Admin"
            style={maxHeight === undefined ? undefined : { maxHeight }}
            className="hidden xl:block xl:sticky xl:top-0 xl:self-start xl:overflow-y-auto w-52 shrink-0 space-y-3"
          >
            {groups.map((group) => {
              const labelId = `admin-area-group-${group.id}`
              return (
                <div key={group.id} role="group" aria-labelledby={labelId}>
                  {/* The record lane's section-title style (RecordSection): uppercase and
                      letter-spaced, so a label never reads as one more row. leading-4 keeps
                      the 16px line text-xs had, which the height budget above counts. */}
                  <div id={labelId} className="px-3 pb-1 text-[11px] leading-4 font-semibold uppercase tracking-[.08em] text-muted-foreground">
                    {group.label}
                  </div>
                  <div className="space-y-0.5">
                    {group.rows.map((row) => (
                      <NavLink
                        key={row.id}
                        to={row.to}
                        data-tour-nav={row.to}
                        className={({ isActive }) =>
                          cn(
                            'block truncate rounded-md px-3 py-1 text-sm transition-colors',
                            isActive
                              ? 'bg-primary/10 font-medium text-primary'
                              : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                          )
                        }
                      >
                        {row.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              )
            })}
          </nav>
        </>
      )}
      <div className="min-w-0 flex-1">
        <Outlet context={context} />
      </div>
    </div>
  )
}

/**
 * The /admin index: the viewer's first row, else home. It needs no guard of
 * its own (SA-1115): each page keeps its guard, so a viewer is only sent to a
 * page they may open.
 */
export function AdminIndex() {
  const context = useOutletContext<AdminAreaContext | undefined>()
  return <Navigate to={context?.firstRow ?? '/'} replace />
}
